import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Scissors, Link as LinkIcon, Share2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import {
  listScheduledQuotes,
  getScheduleDayLink,
  getAppSettings,
} from "@/lib/api";
import { Input } from "@/components/ui/input";

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const Schedule = () => {
  const [date, setDate] = useState(todayIso());
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dayToken, setDayToken] = useState("");
  const [workshop, setWorkshop] = useState({ workshop_name: "", workshop_phone: "" });

  useEffect(() => {
    getAppSettings()
      .then((app) => setWorkshop(app || { workshop_name: "", workshop_phone: "" }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    Promise.all([listScheduledQuotes(date), getScheduleDayLink(date)])
      .then(([qs, link]) => {
        setQuotes(qs);
        setDayToken(link.token);
      })
      .catch(() => {
        toast.error("Couldn't load schedule");
        setQuotes([]);
      })
      .finally(() => setLoading(false));
  }, [date]);

  const workshopUrl = useMemo(() => {
    if (!dayToken || !date) return "";
    const origin = (workshop && workshop.public_url) || window.location.origin;
    return `${origin}/workshop/day/${date}/${dayToken}`;
  }, [date, dayToken, workshop]);

  const totalPieces = useMemo(
    () =>
      quotes.reduce(
        (sum, q) =>
          sum +
          (q.rooms || []).reduce(
            (s, r) => s + (r.cut_pieces?.length || 0),
            0
          ),
        0
      ),
    [quotes]
  );

  const copyLink = async () => {
    if (!workshopUrl) return;
    try {
      await navigator.clipboard.writeText(workshopUrl);
      toast.success("Day link copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const sendToPaul = () => {
    if (!workshopUrl) return;
    const dayLabel = fmtDate(date);
    const jobs = quotes
      .map((q, i) => {
        const pieces = (q.rooms || []).reduce(
          (s, r) => s + (r.cut_pieces?.length || 0),
          0
        );
        return `${i + 1}. ${q.client_name || "(no name)"} — ${pieces} pieces`;
      })
      .join("\n");
    const text =
      `*Cut day — ${dayLabel}*\n\n${quotes.length} job${quotes.length === 1 ? "" : "s"}, ${totalPieces} pieces total\n\n${jobs}`;
    const digits = (workshop.workshop_phone || "").replace(/[^0-9]/g, "");
    const url = digits
      ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="schedule-page">
      <div className="label-xs mb-2">MODULE 05 / SCHEDULE</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9]">
        Cut day planner.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl">
        Every customer below is a <b>cut sheet</b> ready for the workshop —
        tap any row to open the cut sheet for that job. Send the combined list
        to the workshop with one link.
      </p>

      {/* Date selector + summary */}
      <div className="mt-8 border border-zinc-200 p-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <label className="label-xs block mb-2">Cut date</label>
            <div className="flex items-center gap-3">
              <Calendar size={16} className="text-zinc-500" />
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="schedule-date-input"
                className="h-10 w-[200px] rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-[#09090b] font-mono"
              />
            </div>
            <div className="font-mono text-xs text-zinc-500 mt-2">
              {fmtDate(date)}
            </div>
          </div>

          <div className="flex-1 min-w-[220px]">
            <div className="label-xs">Scheduled jobs</div>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="font-display text-4xl font-black tracking-tighter">
                {quotes.length}
              </span>
              <span className="font-mono text-xs text-zinc-500">
                {totalPieces} pieces total
              </span>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={copyLink}
              disabled={!workshopUrl || quotes.length === 0}
              className="btn-sharp secondary disabled:opacity-40"
              data-testid="copy-day-link-btn"
              title="Copy a password-free link that shows all jobs for this day"
            >
              <LinkIcon size={14} /> Copy link
            </button>
            <button
              type="button"
              onClick={sendToPaul}
              disabled={!workshopUrl || quotes.length === 0}
              className="btn-sharp disabled:opacity-40"
              style={{ background: "#25D366", borderColor: "#25D366", color: "white" }}
              data-testid="send-day-to-workshop-btn"
            >
              <Share2 size={14} />{" "}
              Send to {workshop.workshop_name || "workshop"}
            </button>
          </div>
        </div>
      </div>

      {/* Quote list */}
      <div className="mt-8">
        {loading ? (
          <div className="font-mono text-xs text-zinc-400 uppercase tracking-[0.18em] py-10 text-center">
            Loading…
          </div>
        ) : quotes.length === 0 ? (
          <div
            className="border border-dashed border-zinc-300 p-10 text-center"
            data-testid="schedule-empty"
          >
            <Scissors
              size={20}
              className="mx-auto text-zinc-300 mb-3"
            />
            <div className="font-display text-xl font-black tracking-tighter">
              Nothing scheduled for this date
            </div>
            <p className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">
              Open a confirmed quote and tap <b>Schedule cut day</b> to queue it
              here.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-200 border border-zinc-200">
            {quotes.map((q) => {
              const pieces = (q.rooms || []).reduce(
                (s, r) => s + (r.cut_pieces?.length || 0),
                0
              );
              const doneCount = (q.cut_checks || []).length;
              return (
                <li key={q.id}>
                  <Link
                    to={`/quotes/${q.id}#cut`}
                    data-testid={`schedule-quote-${q.id}`}
                    className="flex items-center justify-between gap-4 p-5 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-display text-xl font-black tracking-tighter truncate">
                          {q.client_name || "(Unnamed)"}
                        </div>
                        <span
                          data-testid={`cut-sheet-badge-${q.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[#09090b] text-white font-mono text-[10px] font-bold tracking-[0.18em] uppercase border border-[#09090b]"
                        >
                          <Scissors size={11} strokeWidth={2.5} />
                          Cut sheet
                        </span>
                      </div>
                      <div className="font-mono text-xs text-zinc-500 mt-1 flex flex-wrap gap-x-3">
                        {q.area ? <span>{q.area}</span> : null}
                        <span>{(q.rooms || []).length} rooms</span>
                        <span>
                          {doneCount}/{pieces} pieces cut
                        </span>
                        {q.produced_at ? (
                          <span className="text-green-600">DONE</span>
                        ) : null}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-zinc-400 flex-none" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Schedule;
